import { UserIcon } from "@heroicons/react/20/solid";
import { EllipsisHorizontalIcon } from "@heroicons/react/24/outline";
import classNames from "classnames";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Avatar from "../../../components/common/Avatar";
import PageHeading from "../../../components/common/PageHeading";
import MediaUploader from "../../../components/features/MediaUploader";
import Button from "../../../components/form/Button";
import Dropdown from "../../../components/form/Dropdown";
import { RoleEnum, RoleMap } from "../../../enums/user";
import { Account } from "../../../models";
import { useAppSelector } from "../../../redux/hooks";
import { useUpdateAccountMutation } from "../../../redux/services/account";
import { useGetUserQuery } from "../../../redux/services/user";
import { RootState } from "../../../redux/store";
import { uploadAvatar } from "../../../utils";
import TemplateContainer from "../../TemplateForm/components/common/TemplateContainer";
import EditAccount from "./EditAccount";

const headers: { key: keyof Account; label: string }[] = [
  { key: "companyName", label: "Company Name" },
  { key: "roleId", label: "Role" },
  { key: "timezone", label: "Timezone" },
];

const Accounts = () => {
  const { data: user, isLoading: isLoadingUser } = useGetUserQuery();
  const authToken = useAppSelector((state: RootState) => state.auth.token);
  const [
    updateAccountById,
    {
      isLoading: isLoadingUpdateAccount,
      isSuccess: isSuccessUpdateAccount,
      isError: isErrorUpdateAccount,
      error: errorUpdateAccount,
    },
  ] = useUpdateAccountMutation();

  const [account, setAccount] = useState<Account>();
  const [editAccount, setEditAccount] = useState<boolean>();
  const [editAccountImage, setEditAccountImage] = useState<boolean>();

  useEffect(() => {
    if (isSuccessUpdateAccount) {
      toast.success("Account updated");
      setEditAccountImage(false);
    }
  }, [isSuccessUpdateAccount]);

  useEffect(() => {
    if (isErrorUpdateAccount) {
      toast.error(
        (errorUpdateAccount as any)?.data?.detail ?? "Failed to update"
      );
    }
  }, [isErrorUpdateAccount, errorUpdateAccount]);

  if (isLoadingUser) return null;

  const accounts = user?.accounts ?? [];

  const handleAccountImageUpload = async (file: any, b: any) => {
    if (!authToken) return;

    const uploadedFileURL = await uploadAvatar(file, authToken);
    if (uploadedFileURL) {
      await updateAccountById({
        accountId: account?.accountId,
        companyLogoUrl: uploadedFileURL,
      })
        .unwrap()
        .catch(() => {});
    }
  };

  const handleAccountImageDelete = async () => {
    if (!authToken) return;

    await updateAccountById({
      accountId: account?.accountId,
      companyLogoUrl: "",
    })
      .unwrap()
      .catch(() => {});
  };

  return (
    <TemplateContainer>
      <PageHeading heading="Accounts" />
      <div className="inline-block min-w-full py-2 align-middle">
        <table className="min-w-full border-separate border-spacing-0">
          <thead>
            <tr>
              {headers.map(({ key, label }) => (
                <Header
                  key={key}
                  header={label}
                />
              ))}
              <Header header={""} />
            </tr>
          </thead>
          <tbody>
            {accounts.map((account: Account, index: number) => (
              <tr key={index}>
                {headers.map(({ key }: { key: keyof Account }) => {
                  let data: any = account[key];
                  if (key === "roleId") {
                    data = (
                      <div className="flex gap-1 items-center text-gray-600">
                        <UserIcon className="h-4 w-4 " />
                        {RoleMap[data as RoleEnum]}
                      </div>
                    );
                  } else if (key === "companyName") {
                    data = (
                      <div className="flex gap-2 items-center">
                        <Avatar
                          round
                          name={account?.companyName}
                          src={account.companyLogoUrl}
                          size="32px"
                        />
                        <div className="text-gray-600 max-w-[5rem] md:max-w-md lg:max-w-lg truncate">
                          {account[key]}
                        </div>
                      </div>
                    );
                  }
                  return (
                    <td
                      key={key}
                      className="divide-y divide-y-200 bg-white whitespace-nowrap py-4 text-sm font-medium text-gray-600"
                    >
                      {data}
                    </td>
                  );
                })}
                <Data
                  className="text-end"
                  data={
                    <Dropdown
                      position="right"
                      button={
                        <Button
                          variant="icon"
                          onClick={() => {}}
                        >
                          <EllipsisHorizontalIcon className="size-6 text-gray-900" />
                        </Button>
                      }
                      menuItems={[
                        { key: "edit", label: "Edit" },
                        { key: "changePhoto", label: "Change Photo" },
                      ]}
                      onClick={(key: string) => {
                        setAccount(account);
                        if (key === "edit") {
                          setEditAccount(true);
                        } else if (key === "changePhoto") {
                          setEditAccountImage(true);
                        }
                      }}
                    />
                  }
                />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editAccount && (
        <EditAccount
          account={account as Account}
          loading={isLoadingUpdateAccount}
          errorMessage={""}
          onRequestClose={() => {
            setAccount(undefined);
            setEditAccount(false);
          }}
        />
      )}
      {editAccountImage && (
        <MediaUploader
          heading="Upload Photo"
          src={account?.companyLogoUrl ?? ""}
          loading={false}
          onClose={() => setEditAccountImage(false)}
          onUpload={handleAccountImageUpload}
          onDelete={handleAccountImageDelete}
        />
      )}
    </TemplateContainer>
  );
};

const Header = ({ header }: { header: string }) => (
  <th
    scope="col"
    className="uppercase text-2xs tracking-wide sticky top-0 z-10 border-b border-gray-200 bg-white bg-opacity-75 py-3.5 text-left text-gray-700 backdrop-blur backdrop-filter"
  >
    {header}
  </th>
);

const Data = ({ data, className }: { data: any; className?: string }) => {
  return (
    <td
      className={classNames(
        "divide-y divide-y-200 bg-white whitespace-nowrap py-4 text-sm font-medium text-gray-600",
        className
      )}
    >
      {data}
    </td>
  );
};

export default Accounts;
